import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { buildAccessIndex, canViewBereich as resolveBereich, canViewPage as resolvePage, canViewPath as resolvePath, erlaubteProdukte, type AccessIndex, type AdminProdukt } from "@/lib/adminAccess";

export interface PageAccessRow {
  page_key: string;
  role: string;
  can_view: boolean;
}

/**
 * Wertet die page_access-Matrix für die Rollen EINER Person aus.
 *
 * Brain-Sichtbarkeit bei MEHRFACH-Rollen (Noah 10.08.2026): wer walker UND eine
 * höhere Rolle hat, soll sehen, was die höhere Rolle sieht. Vorher hat EIN
 * can_view = false irgendeiner Rolle die Metrik zugemacht — Paul (admin + walker)
 * sah dadurch keine einzige Kennzahl, obwohl in /admin/page-access bei "Admin"
 * alles freigegeben war.
 *
 * Regel jetzt: pro Metrik über die Rollen der Person VEREINIGEN. Eine Rolle darf
 * sehen, wenn sie entweder keine Zeile hat (Default sichtbar — genau das zeigt die
 * Matrix unter /admin/page-access als Häkchen) oder can_view = true. Verborgen ist
 * eine Metrik also nur, wenn ALLE Rollen der Person explizit auf false stehen.
 *
 * Als reine Funktion exportiert, damit die Rechte-Logik testbar bleibt.
 */
export function resolveAccess(rows: PageAccessRow[], myRoles: Set<string>) {
  const pageMap: Record<string, boolean> = {};
  const brainRows = new Map<string, Map<string, boolean>>();
  const allowedBrainKeys = new Set<string>();

  for (const row of rows) {
    if (!myRoles.has(row.role)) continue;
    if (row.can_view) pageMap[row.page_key] = true;
    if (!row.page_key.startsWith("brain.")) continue;

    const key = row.page_key.slice("brain.".length);
    let perRole = brainRows.get(key);
    if (!perRole) { perRole = new Map<string, boolean>(); brainRows.set(key, perRole); }
    perRole.set(row.role, row.can_view);
    if (row.can_view) allowedBrainKeys.add(key);
  }

  const deniedBrainKeys = new Set<string>();
  const myRoleList = Array.from(myRoles);
  brainRows.forEach((perRole, key) => {
    // get(role) === undefined → keine Zeile → diese Rolle darf sehen.
    const visibleForSomeRole = myRoleList.some((r) => perRole.get(r) !== false);
    if (!visibleForSomeRole) deniedBrainKeys.add(key);
  });

  return { pageMap, deniedBrainKeys, allowedBrainKeys };
}

interface RollenZuweisung { role: string; product_key: string | null }
interface AccessAntwort { employee_id: string | null; roles: RollenZuweisung[]; page_access: PageAccessRow[] }

/** RPC noch nicht eingespielt (PostgREST: Funktion unbekannt) → alter Weg statt Fehler. */
function rpcFehlt(err: { code?: string; message?: string }) {
  return err.code === "PGRST202" || err.code === "42883" || /could not find the function/i.test(err.message ?? "");
}

export function usePageAccess() {
  const [map, setMap] = useState<Record<string, boolean>>({});
  const [isFounder, setIsFounder] = useState(false);
  const [roles, setRoles] = useState<Set<string>>(new Set());
  const [deniedBrainKeys, setDeniedBrainKeys] = useState<Set<string>>(new Set());
  // Brain-Keys, die für eine Rolle des Nutzers EXPLIZIT auf can_view = true stehen.
  // Hebt das founderOnly-Flag einer Metrik auf (siehe AdminOverview).
  const [allowedBrainKeys, setAllowedBrainKeys] = useState<Set<string>>(new Set());
  const [index, setIndex] = useState<AccessIndex>(new Map());
  // Produkte der Person aus ihren Rollen-Zuweisungen (Roadmap 1.2b). Leer = keins.
  const [produkte, setProdukte] = useState<Set<AdminProdukt>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const leer = (mitFehler: boolean) => {
      if (cancelled) return;
      setMap({}); setIsFounder(false); setRoles(new Set()); setDeniedBrainKeys(new Set());
      if (mitFehler) setError(true);
      setLoading(false);
    };

    const anwenden = (rollen: RollenZuweisung[], pa: PageAccessRow[]) => {
      const founder = rollen.some((r) => r.role === "founder");
      const myRoles = new Set(rollen.map((r) => r.role));
      const { pageMap, deniedBrainKeys: denied, allowedBrainKeys: allowed } = resolveAccess(pa, myRoles);
      if (cancelled) return;
      setIsFounder(founder);
      setRoles(myRoles);
      setProdukte(erlaubteProdukte(rollen.map((r) => ({ role: r.role, productKey: r.product_key }))));
      setMap(pageMap);
      setIndex(buildAccessIndex(pa));
      setDeniedBrainKeys(founder ? new Set() : denied);
      setAllowedBrainKeys(allowed);
      setLoading(false);
    };

    // Alter Weg (drei Abfragen nacheinander). Nur noch Rückfall, solange die RPC
    // current_user_access (Migration 20261011093100) live fehlt.
    const alterWeg = async () => {
      // Resolve the current user's employee id via the SECURITY DEFINER fn.
      // (Avoids the ambiguous employees embed — staff_role_assignments has TWO
      // FKs to employees: employee_id AND granted_by.)
      const { data: empId, error: empErr } = await supabase.rpc("current_employee_id");
      if (empErr || !empId) return leer(!!empErr);

      const { data: rows, error: rolesError } = await supabase
        .from("staff_role_assignments")
        .select("role, products:product_id (key)")
        .eq("employee_id", empId);
      if (rolesError) return leer(true);

      const rollen = (rows ?? []).map((r) => ({
        role: r.role as string,
        product_key: (r.products as { key: string } | null)?.key ?? null,
      }));

      const { data: pa, error: paError } = await supabase.from("page_access").select("page_key, role, can_view");
      if (paError) {
        // Wie bisher: Rollen bleiben gesetzt, nur die Matrix fehlt.
        if (cancelled) return;
        setIsFounder(rollen.some((r) => r.role === "founder"));
        setRoles(new Set(rollen.map((r) => r.role)));
        setProdukte(erlaubteProdukte(rollen.map((r) => ({ role: r.role, productKey: r.product_key }))));
        setMap({}); setError(true); setLoading(false);
        return;
      }

      anwenden(rollen, pa ?? []);
    };

    (async () => {
      // Session lokal lesen statt getUser() (spart einen Roundtrip). Die RPC prüft
      // serverseitig selbst über auth.uid(), ein abgelaufenes Token liefert dort nichts.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return leer(false);

      // Ein Roundtrip: Rollen + page_access-Matrix in einem Aufruf.
      const { data, error: rpcError } = await supabase.rpc("current_user_access");
      if (rpcError) {
        if (rpcFehlt(rpcError)) return alterWeg();
        return leer(true);
      }
      const antwort = (data ?? null) as unknown as AccessAntwort | null;
      if (!antwort?.employee_id) return leer(false);
      anwenden(antwort.roles ?? [], antwort.page_access ?? []);
    })();
    return () => { cancelled = true; };
  }, []);

  const canView = (key: string) => isFounder || !!map[key];
  // Mit Vererbung (Roadmap 1.21): eigene Zeile der Seite, sonst die der Gruppe. Siehe src/lib/adminAccess.ts.
  const ctx = useMemo(() => ({ index, roles, isFounder }), [index, roles, isFounder]);
  const canViewPage = (pageKey: string, groupKey?: string) => resolvePage(ctx, pageKey, groupKey);
  const canViewPath = (pathname: string) => resolvePath(ctx, pathname);
  // Bereiche innerhalb einer Seite (CO-8), Vererbung Unterbereich → Bereich → Seite → Gruppe.
  const canViewBereich = (key: string) => resolveBereich(ctx, key);
  return { canView, canViewPage, canViewPath, canViewBereich, accessContext: ctx, isFounder, roles, produkte, deniedBrainKeys, allowedBrainKeys, loading, error };
}
